import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { usePaymentPolling } from '../../../../hooks/usePaymentPolling';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { loadUserInformation, selectUserInformation } from '../../../../store/reducers';
import type { Payment } from '../../../../types/user.types';

type PollingStage = 'POLLING_PAYMENTS' | 'POLLING_USER_INFO' | 'SUCCESS' | 'ERROR';

type LinkedItem = {
  type: 'appointment' | 'fup' | null;
  id: number | null;
  productCode?: string;
};

const PollingPaymentsComponent = () => (
  <div className='flex items-center gap-3 p-4 text-sm text-blue-800 bg-blue-100 border border-blue-200 rounded-lg'>
    <Loader2 className='w-5 h-5 animate-spin' />
    <div>
      <p className='font-semibold'>Ihre Zahlung wird verarbeitet...</p>
      <p>Wir warten auf die Bestätigung des Zahlungsanbieters. Dies kann ein paar Minuten dauern.</p>
    </div>
  </div>
);

const FinalizingComponent = ({ itemType }: { itemType: LinkedItem['type'] }) => (
  <div className='flex items-center gap-3 p-4 text-sm text-blue-800 bg-blue-100 border border-blue-200 rounded-lg'>
    <Loader2 className='w-5 h-5 animate-spin' />
    <div>
      <p className='font-semibold'>
        {itemType === 'fup' ? 'Ihr Folgerezept wird ausgestellt...' : 'Ihr Termin wird abgeschlossen...'}
      </p>
      <p>
        {itemType === 'fup'
          ? 'Zahlung bestätigt. Ihre Rezeptdetails werden jetzt aktualisiert.'
          : 'Zahlung bestätigt. Ihre Termindetails werden jetzt aktualisiert.'}
      </p>
    </div>
  </div>
);

const SuccessComponent = () => (
  <div className='flex items-center gap-3 p-4 text-sm text-green-800 bg-green-100 border border-green-200 rounded-lg'>
    <CheckCircle2 className='w-5 h-5' />
    <div>
      <p className='font-semibold'>Zahlung erfolgreich!</p>
      <p>Ihre Zahlung wurde bestätigt und Ihre Daten sind auf dem neuesten Stand.</p>
    </div>
  </div>
);

const ErrorComponent = ({ message }: { message: string }) => (
  <div className='flex items-center gap-3 p-4 text-sm text-red-800 bg-red-100 border border-red-200 rounded-lg'>
    <XCircle className='w-5 h-5' />
    <div>
      <p className='font-semibold'>Zahlungsfehler</p>
      <p>{message}</p>
    </div>
  </div>
);

export function PaymentStatusPoller({ onComplete, paymentId }: { onComplete: () => void; paymentId: number | null }) {
  const [stage, setStage] = useState<PollingStage>('POLLING_PAYMENTS');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [linkedItem, setLinkedItem] = useState<LinkedItem>({ type: null, id: null, productCode: undefined });

  const dispatch = useAppDispatch();
  const userInformation = useAppSelector(selectUserInformation);

  if (!paymentId) {
    return <ErrorComponent message='Keine gültige Zahlungs-ID vorhanden.' />;
  }

  const handlePaymentSuccess = (payment: Payment) => {
    if (payment.appointmentId) {
      setLinkedItem({ type: 'appointment', id: payment.appointmentId, productCode: payment.productCode });
    } else if (payment.followupPrescriptionId) {
      setLinkedItem({ type: 'fup', id: payment.followupPrescriptionId, productCode: payment.productCode });
    } else {
      setStage('SUCCESS');
      onComplete();

      return;
    }
    setStage('POLLING_USER_INFO');
  };

  const handleError = (message: string) => {
    setErrorMessage(message);
    setStage('ERROR');
    onComplete();
  };

  const isPollingPayments = stage === 'POLLING_PAYMENTS';
  usePaymentPolling({
    onSuccess: handlePaymentSuccess,
    onError: handleError,
    interval: 4000,
    paymentId: paymentId,
    enabled: isPollingPayments,
  });

  useEffect(() => {
    if (stage !== 'POLLING_USER_INFO' || !linkedItem.type) {
      return;
    }

    let isProductStatusCorrect = false;
    const isReservation = linkedItem.productCode?.includes('reservation');

    const pollUserInfo = () => {
      loadUserInformation(dispatch);
    };

    if (!isReservation) {
      // Polling user info to update userinformation - this stops "outstanding payments" from appearing on dashboard
      pollUserInfo();
      setStage('SUCCESS');
      onComplete();

      return;
    }

    if (linkedItem.type === 'appointment') {
      // @ts-expect-error appointment id type is string but should be number, fixing this needs changes accross the codebase
      const appointment = userInformation.appointment?.find((a) => a.id === linkedItem.id);

      isProductStatusCorrect = !!appointment && appointment.carriedOut === undefined;
    } else if (linkedItem.type === 'fup') {
      const fup = userInformation.followUpPrescriptionRequest?.find((f) => f.id === linkedItem.id);

      isProductStatusCorrect = !!fup && fup.status === undefined;
    }

    if (isProductStatusCorrect) {
      setStage('SUCCESS');
      onComplete();

      return;
    }

    const interval = setInterval(pollUserInfo, 5000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      handleError(
        'Die Aktualisierung des Termins/Rezepts konnte nicht bestätigt werden. Bitte aktualisieren Sie die Seite.'
      );
    }, 100000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [stage, userInformation, linkedItem, dispatch, onComplete]);

  switch (stage) {
    case 'POLLING_PAYMENTS':
      return <PollingPaymentsComponent />;
    case 'POLLING_USER_INFO':
      return <FinalizingComponent itemType={linkedItem.type} />;
    case 'SUCCESS':
      return <SuccessComponent />;
    case 'ERROR':
      return <ErrorComponent message={errorMessage} />;
    default:
      return null;
  }
}
